import { Collection, type Feature } from 'ol';
import Geometry, { type Type as GeometryType } from 'ol/geom/Geometry';
import Draw from 'ol/interaction/Draw';
import Modify from 'ol/interaction/Modify';
import Snap from 'ol/interaction/Snap';
import React, { Dispatch, useCallback, useEffect, useState } from 'react';
import { useOpenLayersContext } from '../OpenLayersContext';
import { createVectorSource } from './createVectorSource';
import VectorSource from 'ol/source/Vector';
import VectorLayer from 'ol/layer/Vector';
import { useLayers } from '../layerManagement/LayerContext';
import { v4 as uuidv4 } from 'uuid';
import { isValidGeometry } from '../isValidGeometry';
import { throttle } from '@ais-ui/utils';
import { DrawingStyles, getDrawingStyle } from '../styles/getDrawingStyle';
import { GeometryValidation } from './DrawingContext';
import { Circle } from 'ol/geom';
import OlGeometry from 'ol/geom/Geometry';

export function useDrawFeature(
  geometryType: GeometryType | null | undefined,
  onDrawModify: ((feature: Feature) => void) | null,
  onDrawComplete: ((feature: Feature) => void) | null, // Can still modify shape when drawing is complete
  existingFeature: Feature | null | undefined,
  setIsValidDrawing: Dispatch<React.SetStateAction<GeometryValidation>>,
  drawingStyle: DrawingStyles,
  maxPoints?: number,
  setRadius?: Dispatch<React.SetStateAction<number | undefined>>,
  validateWhileDrawing = false,
) {
  const { map, getCircleTrueRadius } = useOpenLayersContext();
  const [isFinished, setIsFinished] = useState(false);
  const [draw, setDraw] = useState<Draw | null>(null);
  const { addLayer, removeLayer } = useLayers();
  const [source, setSource] = useState<VectorSource<Feature<Geometry>> | null>(
    null,
  );

  const clearDrawing = useCallback(() => {
    if (source) {
      source.clear();
      setSource(null);
    }
    if (draw) {
      map.removeInteraction(draw);
      setDraw(null);
    }

    setIsValidDrawing({ isValid: false, reason: 'No shape drawn.' });
  }, [source, draw, setIsValidDrawing, map]);

  const finishDrawing = useCallback(() => {
    if (draw) {
      draw.finishDrawing();
    }
  }, [draw]);

  const [layer, setLayer] = useState<VectorLayer<Feature<OlGeometry>> | null>(
    null,
  );

  const undo = useCallback(() => {
    if (draw) {
      draw.removeLastPoint();
    }
  }, [draw]);

  const replaceFeature = useCallback(
    (newFeature: Feature) => {
      if (source) {
        source.clear();
        newFeature.setStyle(
          getDrawingStyle(drawingStyle, geometryType as GeometryType),
        );
        source.addFeature(newFeature);

        const geom = newFeature.getGeometry();
        setIsValidDrawing(isValidGeometry(geom, maxPoints));

        if (geom?.getType() === 'Circle' && setRadius) {
          const circle = geom as Circle;
          setRadius(Math.round(getCircleTrueRadius(circle)));
        }
      }
    },
    [
      source,
      drawingStyle,
      geometryType,
      setIsValidDrawing,
      maxPoints,
      setRadius,
      getCircleTrueRadius,
    ],
  );

  const [drawingId] = useState(uuidv4());

  useEffect(() => {
    if (layer) {
      addLayer(layer, 'Drawing layer', false, false, false, {}, drawingId);
    }

    return () => {
      if (layer) {
        removeLayer(layer);
      }
    };
  }, [addLayer, drawingId, layer, removeLayer]);

  useEffect(() => {
    if (!geometryType) {
      if (layer) {
        removeLayer(layer);
        setLayer(null);
        setSource(null);
      }

      if (draw) {
        map.removeInteraction(draw);
        setDraw(null);
      }

      return;
    }

    if (!source || !layer) {
      const { source: newSource, layer: newLayer } = createVectorSource();
      setSource(newSource);
      setLayer(newLayer);
    }
  }, [map, geometryType, source, layer, removeLayer, draw]);

  useEffect(() => {
    if (!source || !geometryType || !layer) {
      setIsFinished(false);
      return;
    }

    if (existingFeature) {
      existingFeature.setStyle(getDrawingStyle(drawingStyle, geometryType));
      source.addFeature(existingFeature);
      const geom = existingFeature.getGeometry();
      setIsValidDrawing(isValidGeometry(geom, maxPoints));
    }

    const modify = new Modify({ source });
    map.addInteraction(modify);

    modify.on('modifyend', function (event) {
      const features: Collection<Feature<Geometry>> = event.features;
      const featuresArray = features.getArray();
      if (featuresArray.length === 0) {
        return;
      }
      const feature = featuresArray[0];
      const geom = feature.getGeometry();
      setIsValidDrawing(isValidGeometry(geom, maxPoints));

      if (geom?.getType() === 'Circle' && setRadius) {
        const circle = geom as Circle;
        setRadius(Math.round(getCircleTrueRadius(circle)));
      }

      if (onDrawModify) {
        onDrawModify(feature);
      }
    });

    const snap = new Snap({ source });
    map.addInteraction(snap);

    if (!draw) {
      const newDraw = new Draw({
        source: source,
        type: geometryType,
        style: getDrawingStyle(drawingStyle, geometryType),
        maxPoints,
      });

      let lastLength = 0;

      newDraw.on('drawstart', function (event) {
        const feature = event.feature;
        const geometry = feature.getGeometry();
        if (!geometry) {
          return;
        }
        // For LineString, don't set as valid until finished
        if (geometry.getType() === 'LineString') {
          setIsValidDrawing({ isValid: false, reason: 'Drawing in progress' });
        } else {
          setIsValidDrawing(isValidGeometry(geometry, maxPoints));
        }

        geometry.on(
          'change',
          throttle(function (event) {
            const geom = event.target;
            let coordinates;

            switch (geom.getType()) {
              case 'Point':
                coordinates = [geom.getCoordinates()];
                break;
              case 'LineString':
                coordinates = geom.getCoordinates();
                // Only check isFinished if not validateWhileDrawing
                if (!validateWhileDrawing && !isFinished) {
                  return;
                }
                break;
              case 'Polygon':
                coordinates = geom.getCoordinates()[0];
                break;
              case 'Circle':
                coordinates = [geom.getCenter()];
                if (setRadius) {
                  setRadius(Math.round(getCircleTrueRadius(geom as Circle)));
                }
                break;
              default:
                return;
            }
            setIsValidDrawing(isValidGeometry(geom, maxPoints));

            if (coordinates.length !== lastLength) {
              if (onDrawModify) {
                onDrawModify(feature);
              }

              lastLength = coordinates.length;
            }
          }, 20),
        );
      });
      newDraw.on('drawend', (event) => {
        setIsFinished(true);
        const feature = event.feature;
        const geometry = feature.getGeometry();

        if (geometry) {
          setIsValidDrawing(isValidGeometry(geometry, maxPoints));
        }

        feature.setStyle(getDrawingStyle(drawingStyle, geometryType));

        if (onDrawComplete) {
          onDrawComplete(event.feature);
        }
        if (newDraw) {
          map.removeInteraction(newDraw);
        }
      });

      setDraw(newDraw);
      map.addInteraction(newDraw);
    }

    return () => {
      if (existingFeature) {
        source.removeFeature(existingFeature);
      }
      map.removeInteraction(modify);
      map.removeInteraction(snap);
      if (draw) {
        map.removeInteraction(draw);
      }
    };
  }, [
    map,
    geometryType,
    existingFeature,
    source,
    layer,
    onDrawModify,
    onDrawComplete,
    setIsValidDrawing,
    draw,
    setRadius,
    drawingStyle,
    getCircleTrueRadius,
    isFinished,
    maxPoints,
    validateWhileDrawing,
  ]);

  return { undo, clearDrawing, finishDrawing, replaceFeature };
}
